import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Download, FileText, Image as ImageIcon, X, Paperclip, BookmarkPlus } from "lucide-react";
import { api, API, fmtMXN, fmtDate, todayISO, daysAgoISO, withToken } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Card, CardContent } from "../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Badge } from "../components/ui/badge";
import { Checkbox } from "../components/ui/checkbox";
import { Switch } from "../components/ui/switch";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "../components/ui/dialog";
import { Tabs, TabsList, TabsTrigger } from "../components/ui/tabs";

const empty = {
    type: "expense",
    car_id: "",
    category_id: "",
    supplier_id: "",
    classification: "operativo",
    amount: "",
    piece_count: 1,
    description: "",
    date: todayISO(),
    receipt_path: "",
};

const PERIODS = [
    { value: "all", label: "Todo el tiempo" },
    { value: "7", label: "Últimos 7 días" },
    { value: "30", label: "Últimos 30 días" },
    { value: "90", label: "Últimos 90 días" },
    { value: "365", label: "Último año" },
    { value: "custom", label: "Personalizado" },
];

export default function Transactions() {
    const { isAdmin, canWrite } = useAuth();
    const [items, setItems] = useState([]);
    const [cars, setCars] = useState([]);
    const [categories, setCategories] = useState([]);
    const [suppliers, setSuppliers] = useState([]);
    const [filterType, setFilterType] = useState("all");
    const [filterCar, setFilterCar] = useState("all");
    const [period, setPeriod] = useState("all");
    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");
    const [open, setOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState(empty);
    const [splitMode, setSplitMode] = useState(false);
    const [splitCarIds, setSplitCarIds] = useState([]);
    const [splitTemplates, setSplitTemplates] = useState([]);
    const [uploadingReceipt, setUploadingReceipt] = useState(false);
    const receiptInputRef = useRef(null);

    useEffect(() => {
        if (period === "all") { setStartDate(""); setEndDate(""); }
        else if (period !== "custom") { setStartDate(daysAgoISO(Number(period))); setEndDate(todayISO()); }
    }, [period]);

    const loadAll = async () => {
        const params = {};
        if (filterType !== "all") params.type = filterType;
        if (filterCar !== "all") params.car_id = filterCar;
        if (startDate) params.start_date = startDate;
        if (endDate) params.end_date = endDate;
        const [tx, c, ct, sp, tpl] = await Promise.all([
            api.get("/transactions", { params }),
            api.get("/cars"),
            api.get("/categories"),
            api.get("/suppliers"),
            api.get("/split-templates"),
        ]);
        setItems(tx.data);
        setCars(c.data);
        setCategories(ct.data);
        setSuppliers(sp.data);
        setSplitTemplates(tpl.data);
    };

    useEffect(() => {
        loadAll();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filterType, filterCar, startDate, endDate]);

    const buildExportUrl = (kind) => {
        const params = new URLSearchParams();
        if (filterType !== "all") params.set("type", filterType);
        if (filterCar !== "all") params.set("car_id", filterCar);
        if (startDate) params.set("start_date", startDate);
        if (endDate) params.set("end_date", endDate);
        return `${API}/export/${kind}?${params.toString()}`;
    };

    const openNew = () => {
        setEditing(null);
        setForm({ ...empty, car_id: cars[0]?.id || "" });
        setSplitMode(false);
        setSplitCarIds([]);
        setOpen(true);
    };

    const openEdit = (t) => {
        setEditing(t);
        setForm({
            type: t.type,
            car_id: t.car_id,
            category_id: t.category_id || "",
            supplier_id: t.supplier_id || "",
            classification: t.classification || "operativo",
            amount: t.amount,
            piece_count: t.piece_count || 1,
            description: t.description || "",
            date: t.date,
            receipt_path: t.receipt_path || "",
        });
        setSplitMode(false);
        setSplitCarIds([]);
        setOpen(true);
    };

    const handleReceiptUpload = async (file) => {
        if (!file) return;
        setUploadingReceipt(true);
        const fd = new FormData();
        fd.append("file", file);
        try {
            const { data } = await api.post("/uploads/receipt", fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            setForm((f) => ({ ...f, receipt_path: data.path }));
            toast.success("Recibo cargado");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Error al subir recibo");
        } finally {
            setUploadingReceipt(false);
            if (receiptInputRef.current) receiptInputRef.current.value = "";
        }
    };

    const toggleSplitCar = (carId) => {
        setSplitCarIds((prev) => prev.includes(carId) ? prev.filter((id) => id !== carId) : [...prev, carId]);
    };

    const applyTemplate = (tpl) => {
        setSplitCarIds(tpl.car_ids.filter((id) => cars.some((c) => c.id === id)));
    };

    const saveCurrentAsTemplate = async () => {
        if (splitCarIds.length < 2) return;
        const name = window.prompt("Nombre de la plantilla (ej. Toda la flotilla)");
        if (!name || !name.trim()) return;
        try {
            const { data } = await api.post("/split-templates", { name: name.trim(), car_ids: splitCarIds });
            setSplitTemplates((prev) => [...prev, data].sort((a, b) => a.name.localeCompare(b.name)));
            toast.success("Plantilla guardada");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Error al guardar la plantilla");
        }
    };

    const deleteTemplate = async (tpl) => {
        if (!window.confirm(`¿Eliminar la plantilla "${tpl.name}"?`)) return;
        try {
            await api.delete(`/split-templates/${tpl.id}`);
            setSplitTemplates((prev) => prev.filter((t) => t.id !== tpl.id));
            toast.success("Plantilla eliminada");
        } catch (e) {
            toast.error("Error al eliminar la plantilla");
        }
    };

    const submit = async () => {
        if (!form.amount || Number(form.amount) <= 0) {
            toast.error("Ingresa un monto válido");
            return;
        }
        if (splitMode) {
            if (splitCarIds.length < 2) {
                toast.error("Selecciona al menos 2 autos para prorratear");
                return;
            }
            const payload = {
                type: form.type,
                car_ids: splitCarIds,
                category_id: form.category_id || null,
                supplier_id: form.supplier_id || null,
                classification: form.classification,
                total_amount: Number(form.amount),
                piece_count: Number(form.piece_count) || 1,
                description: form.description,
                date: form.date,
                receipt_path: form.receipt_path || null,
            };
            try {
                await api.post("/transactions/split", payload);
                toast.success(`Gasto prorrateado entre ${splitCarIds.length} autos`);
                setOpen(false);
                loadAll();
            } catch (e) {
                toast.error(e?.response?.data?.detail || "Error al guardar");
            }
            return;
        }
        if (!form.car_id) {
            toast.error("Selecciona un auto");
            return;
        }
        const payload = {
            ...form,
            amount: Number(form.amount),
            piece_count: Number(form.piece_count) || 1,
            category_id: form.category_id || null,
            supplier_id: form.supplier_id || null,
            receipt_path: form.receipt_path || null,
        };
        try {
            if (editing) {
                await api.put(`/transactions/${editing.id}`, payload);
                toast.success("Movimiento actualizado");
            } else {
                await api.post("/transactions", payload);
                toast.success("Movimiento registrado");
            }
            setOpen(false);
            loadAll();
        } catch (e) {
            toast.error("Error al guardar");
        }
    };

    const remove = async (t) => {
        if (!window.confirm("¿Eliminar este movimiento?")) return;
        await api.delete(`/transactions/${t.id}`);
        toast.success("Eliminado");
        loadAll();
    };

    const filteredCats = categories.filter((c) => c.kind === form.type);

    return (
        <div className="space-y-6" data-testid="transactions-page">
            <header className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
                <div>
                    <p className="text-xs font-semibold tracking-[0.05em] uppercase text-slate-500">Movimientos</p>
                    <h1 className="text-3xl font-semibold tracking-tight text-slate-900 mt-1 font-display">Ingresos y Gastos</h1>
                    <p className="text-sm text-slate-500 mt-1">Registra, edita, filtra y exporta las operaciones de tu flotilla.</p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                    <a href={withToken(buildExportUrl("csv"))} target="_blank" rel="noreferrer" data-testid="export-csv">
                        <Button variant="outline" className="border-slate-200 bg-white">
                            <Download size={14} className="mr-1" /> CSV
                        </Button>
                    </a>
                    <a href={withToken(buildExportUrl("pdf"))} target="_blank" rel="noreferrer" data-testid="export-pdf">
                        <Button variant="outline" className="border-slate-200 bg-white">
                            <FileText size={14} className="mr-1" /> PDF
                        </Button>
                    </a>
                    {canWrite && (
                        <Button onClick={openNew} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="add-transaction-button">
                            <Plus size={16} className="mr-1" /> Nuevo movimiento
                        </Button>
                    )}
                </div>
            </header>

            <Card className="border-slate-200 shadow-sm">
                <CardContent className="p-4">
                    <div className="flex flex-col md:flex-row md:items-center gap-3">
                        <Tabs value={filterType} onValueChange={setFilterType}>
                            <TabsList>
                                <TabsTrigger value="all" data-testid="tab-all">Todos</TabsTrigger>
                                <TabsTrigger value="income" data-testid="tab-income">Ingresos</TabsTrigger>
                                <TabsTrigger value="expense" data-testid="tab-expense">Gastos</TabsTrigger>
                            </TabsList>
                        </Tabs>
                        <Select value={filterCar} onValueChange={setFilterCar}>
                            <SelectTrigger className="w-full md:w-[220px] bg-white" data-testid="filter-car-tx">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">Todos los autos</SelectItem>
                                {cars.map((c) => (
                                    <SelectItem key={c.id} value={c.id}>{c.plate} · {c.brand} {c.model}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        <Select value={period} onValueChange={setPeriod}>
                            <SelectTrigger className="w-full md:w-[180px] bg-white" data-testid="filter-period-tx">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                {PERIODS.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
                            </SelectContent>
                        </Select>
                        {period === "custom" && (
                            <div className="flex items-center gap-2">
                                <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="bg-white" data-testid="filter-start-date-tx" />
                                <span className="text-slate-400">→</span>
                                <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="bg-white" data-testid="filter-end-date-tx" />
                            </div>
                        )}
                    </div>
                </CardContent>
            </Card>

            <Card className="border-slate-200 shadow-sm">
                <CardContent className="p-0">
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="text-xs uppercase bg-slate-50 text-slate-500 tracking-wider">
                                    <th className="text-left px-4 py-3 font-semibold">Fecha</th>
                                    <th className="text-left px-4 py-3 font-semibold">Tipo</th>
                                    <th className="text-left px-4 py-3 font-semibold">Auto</th>
                                    <th className="text-left px-4 py-3 font-semibold">Categoría</th>
                                    <th className="text-left px-4 py-3 font-semibold">Proveedor</th>
                                    <th className="text-left px-4 py-3 font-semibold">Clasif.</th>
                                    <th className="text-right px-4 py-3 font-semibold">Piezas</th>
                                    <th className="text-right px-4 py-3 font-semibold">Monto</th>
                                    <th className="text-center px-4 py-3 font-semibold">Recibo</th>
                                    <th className="px-4 py-3" />
                                </tr>
                            </thead>
                            <tbody>
                                {items.length === 0 && (
                                    <tr><td colSpan={10} className="px-4 py-12 text-center text-slate-400">No hay movimientos en este filtro.</td></tr>
                                )}
                                {items.map((t) => {
                                    const car = cars.find((c) => c.id === t.car_id);
                                    const cat = categories.find((c) => c.id === t.category_id);
                                    const sup = suppliers.find((s) => s.id === t.supplier_id);
                                    return (
                                        <tr key={t.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`row-tx-${t.id}`}>
                                            <td className="px-4 py-3 text-slate-700">{fmtDate(t.date)}</td>
                                            <td className="px-4 py-3">
                                                {t.type === "income" ? (
                                                    <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">Ingreso</Badge>
                                                ) : (
                                                    <Badge className="bg-rose-100 text-rose-700 hover:bg-rose-100">Gasto</Badge>
                                                )}
                                            </td>
                                            <td className="px-4 py-3 text-slate-700">{car ? `${car.plate}` : "—"}</td>
                                            <td className="px-4 py-3 text-slate-700">{cat?.name || "—"}</td>
                                            <td className="px-4 py-3 text-slate-700">{sup?.name || "—"}</td>
                                            <td className="px-4 py-3 text-slate-700 capitalize">{t.classification || "—"}</td>
                                            <td className="px-4 py-3 text-right text-slate-700">{t.piece_count || 1}</td>
                                            <td className={`px-4 py-3 text-right font-semibold ${t.type === "income" ? "text-emerald-600" : "text-rose-600"}`}>
                                                {fmtMXN(t.amount)}
                                                {t.split_group_id && (
                                                    <div
                                                        title={`Total prorrateado: ${fmtMXN(t.split_total_amount)} entre ${(t.split_car_ids || []).length} autos: ${(t.split_car_ids || []).map((id) => cars.find((c) => c.id === id)?.plate || "?").join(", ")}`}
                                                        data-testid={`split-badge-${t.id}`}
                                                    >
                                                        <Badge variant="outline" className="mt-1 font-normal text-[10px] border-slate-300 text-slate-500 cursor-help">
                                                            Prorrateado
                                                        </Badge>
                                                    </div>
                                                )}
                                            </td>
                                            <td className="px-4 py-3 text-center">
                                                {t.receipt_path ? (
                                                    <a href={withToken(`${API}/files/${t.receipt_path}`)} target="_blank" rel="noreferrer" className="inline-block" data-testid={`receipt-${t.id}`}>
                                                        <img src={withToken(`${API}/files/${t.receipt_path}`)} alt="Recibo" className="w-9 h-9 rounded object-cover border border-slate-200 hover:border-slate-400" />
                                                    </a>
                                                ) : (
                                                    <span className="text-slate-300 text-xs">—</span>
                                                )}
                                            </td>
                                            <td className="px-4 py-3 text-right">
                                                <div className="flex justify-end gap-1">
                                                    {canWrite && (
                                                        <button onClick={() => openEdit(t)} className="p-1.5 rounded hover:bg-slate-200" data-testid={`edit-tx-${t.id}`}>
                                                            <Pencil size={14} />
                                                        </button>
                                                    )}
                                                    {isAdmin && (
                                                        <button onClick={() => remove(t)} className="p-1.5 rounded hover:bg-rose-50 text-rose-600" data-testid={`delete-tx-${t.id}`}>
                                                            <Trash2 size={14} />
                                                        </button>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </CardContent>
            </Card>

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="max-w-lg" data-testid="transaction-dialog">
                    <DialogHeader>
                        <DialogTitle className="font-display">{editing ? "Editar movimiento" : "Nuevo movimiento"}</DialogTitle>
                        <DialogDescription>Captura los datos del ingreso o gasto.</DialogDescription>
                    </DialogHeader>
                    <div className="grid grid-cols-2 gap-3">
                        <div className="col-span-2">
                            <Tabs value={form.type} onValueChange={(v) => { setForm({ ...form, type: v, category_id: "" }); if (v !== "expense") { setSplitMode(false); setSplitCarIds([]); } }}>
                                <TabsList className="w-full">
                                    <TabsTrigger value="expense" className="flex-1" data-testid="dialog-tab-expense">Gasto</TabsTrigger>
                                    <TabsTrigger value="income" className="flex-1" data-testid="dialog-tab-income">Ingreso</TabsTrigger>
                                </TabsList>
                            </Tabs>
                        </div>
                        {form.type === "expense" && !editing && (
                            <div className="col-span-2 flex items-center justify-between rounded-md border border-slate-200 bg-slate-50 px-3 py-2">
                                <div>
                                    <Label htmlFor="split-toggle" className="text-xs font-medium text-slate-700">Prorratear entre varios autos</Label>
                                    <p className="text-[11px] text-slate-500">Divide este gasto en partes iguales entre los autos que selecciones.</p>
                                </div>
                                <Switch
                                    id="split-toggle"
                                    checked={splitMode}
                                    onCheckedChange={(v) => { setSplitMode(v); setSplitCarIds([]); }}
                                    data-testid="split-mode-toggle"
                                />
                            </div>
                        )}
                        {splitMode ? (
                            <div className="col-span-2">
                                {splitTemplates.length > 0 && (
                                    <div className="mb-2">
                                        <Label className="text-xs">Plantillas guardadas</Label>
                                        <div className="flex flex-wrap gap-1.5 mt-1" data-testid="split-template-chips">
                                            {splitTemplates.map((tpl) => (
                                                <span
                                                    key={tpl.id}
                                                    className="inline-flex items-center gap-1 rounded-full border border-slate-300 bg-white pl-3 pr-1 py-1 text-xs text-slate-700 hover:border-slate-900"
                                                >
                                                    <button
                                                        type="button"
                                                        onClick={() => applyTemplate(tpl)}
                                                        className="cursor-pointer"
                                                        data-testid={`apply-split-template-${tpl.id}`}
                                                    >
                                                        {tpl.name} · {tpl.car_ids.length} autos
                                                    </button>
                                                    {isAdmin && (
                                                        <button
                                                            type="button"
                                                            onClick={() => deleteTemplate(tpl)}
                                                            className="p-0.5 rounded-full hover:bg-rose-50 text-slate-400 hover:text-rose-600"
                                                            data-testid={`delete-split-template-${tpl.id}`}
                                                        >
                                                            <X size={11} />
                                                        </button>
                                                    )}
                                                </span>
                                            ))}
                                        </div>
                                    </div>
                                )}
                                <Label className="text-xs">Autos a prorratear ({splitCarIds.length} seleccionados)</Label>
                                <div className="mt-1 max-h-36 overflow-y-auto rounded-md border border-slate-200 divide-y divide-slate-100" data-testid="split-car-list">
                                    {cars.map((c) => (
                                        <label key={c.id} className="flex items-center gap-2 px-3 py-2 text-sm cursor-pointer hover:bg-slate-50">
                                            <Checkbox
                                                checked={splitCarIds.includes(c.id)}
                                                onCheckedChange={() => toggleSplitCar(c.id)}
                                                data-testid={`split-car-checkbox-${c.id}`}
                                            />
                                            <span className="text-slate-700">{c.plate} · {c.brand} {c.model}</span>
                                        </label>
                                    ))}
                                </div>
                                <div className="flex items-center justify-between mt-1">
                                    {splitCarIds.length >= 2 && Number(form.amount) > 0 ? (
                                        <p className="text-xs text-slate-500" data-testid="split-preview">
                                            {fmtMXN(Number(form.amount) / splitCarIds.length)} por auto entre {splitCarIds.length} autos
                                        </p>
                                    ) : <span />}
                                    {splitCarIds.length >= 2 && (
                                        <button
                                            type="button"
                                            onClick={saveCurrentAsTemplate}
                                            className="flex items-center gap-1 text-xs text-slate-500 hover:text-slate-900"
                                            data-testid="save-split-template-button"
                                        >
                                            <BookmarkPlus size={12} /> Guardar como plantilla
                                        </button>
                                    )}
                                </div>
                            </div>
                        ) : (
                            <div>
                                <Label className="text-xs">Auto</Label>
                                <Select value={form.car_id} onValueChange={(v) => setForm({ ...form, car_id: v })}>
                                    <SelectTrigger data-testid="form-car"><SelectValue placeholder="Selecciona" /></SelectTrigger>
                                    <SelectContent>
                                        {cars.map((c) => <SelectItem key={c.id} value={c.id}>{c.plate} · {c.brand}</SelectItem>)}
                                    </SelectContent>
                                </Select>
                            </div>
                        )}
                        <div>
                            <Label className="text-xs">Fecha</Label>
                            <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="form-date" />
                        </div>
                        <div>
                            <Label className="text-xs">Categoría</Label>
                            <Select value={form.category_id} onValueChange={(v) => setForm({ ...form, category_id: v })}>
                                <SelectTrigger data-testid="form-category"><SelectValue placeholder="Categoría" /></SelectTrigger>
                                <SelectContent>
                                    {filteredCats.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label className="text-xs">Proveedor</Label>
                            <Select value={form.supplier_id} onValueChange={(v) => setForm({ ...form, supplier_id: v })}>
                                <SelectTrigger data-testid="form-supplier"><SelectValue placeholder="Proveedor" /></SelectTrigger>
                                <SelectContent>
                                    {suppliers.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label className="text-xs">Clasificación</Label>
                            <Select value={form.classification} onValueChange={(v) => setForm({ ...form, classification: v })}>
                                <SelectTrigger data-testid="form-classification"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="operativo">Operativo</SelectItem>
                                    <SelectItem value="administrativo">Administrativo</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label className="text-xs">Piezas</Label>
                            <Input type="number" min="1" value={form.piece_count} onChange={(e) => setForm({ ...form, piece_count: e.target.value })} data-testid="form-pieces" />
                        </div>
                        <div>
                            <Label className="text-xs">{splitMode ? "Monto total (MXN)" : "Monto (MXN)"}</Label>
                            <Input type="number" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="form-amount" />
                        </div>
                        <div className="col-span-2">
                            <Label className="text-xs">Descripción</Label>
                            <Textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="form-description" />
                        </div>
                        <div className="col-span-2">
                            <Label className="text-xs flex items-center gap-1"><Paperclip size={12} /> Recibo / Ticket (JPG, PNG, WEBP · máx. 5 MB)</Label>
                            {form.receipt_path ? (
                                <div className="mt-2 flex items-center gap-3 p-2 rounded-md border border-slate-200 bg-slate-50">
                                    <img src={withToken(`${API}/files/${form.receipt_path}`)} alt="Recibo" className="w-14 h-14 rounded object-cover" />
                                    <div className="flex-1 text-xs text-slate-600 truncate">Recibo adjunto</div>
                                    <button
                                        type="button"
                                        onClick={() => setForm({ ...form, receipt_path: "" })}
                                        className="p-1.5 rounded hover:bg-rose-50 text-rose-600"
                                        data-testid="remove-receipt"
                                    >
                                        <X size={14} />
                                    </button>
                                </div>
                            ) : (
                                <input
                                    ref={receiptInputRef}
                                    type="file"
                                    accept="image/jpeg,image/png,image/webp"
                                    onChange={(e) => handleReceiptUpload(e.target.files?.[0])}
                                    disabled={uploadingReceipt}
                                    className="mt-1 block w-full text-sm text-slate-700 file:mr-3 file:py-2 file:px-4 file:rounded-md file:border-0 file:bg-slate-900 file:text-white hover:file:bg-slate-800 disabled:opacity-50"
                                    data-testid="form-receipt-file"
                                />
                            )}
                            {uploadingReceipt && <p className="text-xs text-slate-500 mt-1">Subiendo…</p>}
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
                        <Button className="bg-slate-900 hover:bg-slate-800 text-white" onClick={submit} data-testid="save-transaction">
                            {editing ? "Guardar cambios" : splitMode ? "Prorratear y registrar" : "Registrar"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
